import {
  Checkbox,
  Dialog,
  Fieldset,
  Heading,
  Link,
  Paragraph,
  Spinner,
} from "@digdir/designsystemet-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { ExternalLinkIcon } from "@navikt/aksel-icons";
import { useNavigate } from "@tanstack/react-router";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type {
  PostAvtalePrivatRequest,
  PostAvtaleSelskapRequest,
} from "@/api/generated";
import { DescriptionList, DescriptionTerm, FormWrapper } from "@/components";
import { useAuth } from "@/features/auth";
import {
  type BestillingAktoerSelskap,
  BestillingFormErrorSummary,
  type BestillingPageKey,
  HeadingWithLink,
  useAktoerData,
  useBestillingContext,
  useLeverandoerData,
  useSaveBestilling,
} from "@/features/bestillinger";
import {
  BestillingNavigationActions,
  FormNavigationBlocker,
} from "@/features/navigation";
import { useToaster } from "@/features/toaster";
import {
  formatDate,
  formatDateStringInterval,
  formatPercentage,
  formatPostnummer,
} from "@/utils";
import { formatAktoerNavn } from "./AktoerFormSchemas";
import {
  getDefaultOppsummeringFormValues,
  oppsummeringFormSchema,
  type oppsummeringFormValues,
} from "./OppsummeringFormSchemas";

const currentPageKey: BestillingPageKey = "/bestilling/ny/oppsummering";

export function OppsummeringForm() {
  const { t } = useTranslation();
  const { account } = useAuth();

  const toast = useToaster();
  const { getLeverandoerByOrgNr, isLoading: leverandrørIsLoading } =
    useLeverandoerData();
  const { getAktoerByOrgNr, isLoading: aktørIsLoading } = useAktoerData();
  const {
    privat: {
      mutateAsync: mutateBestillingPrivatAsync,
      isPending: postPrivatPending,
    },
    selskap: {
      mutateAsync: mutateBestillingSelskapAsync,
      isPending: postSelskapPending,
    },
  } = useSaveBestilling();
  const navigate = useNavigate();

  const { readOnly, bestilling, setBestillingData } = useBestillingContext();

  const form = useForm({
    resolver: zodResolver(oppsummeringFormSchema),
    values: getDefaultOppsummeringFormValues(bestilling),
    disabled: readOnly,
    mode: "all",
  });

  const postBestillingPrivat = async () => {
    if (!bestilling.bestillerPrivat || !bestilling.avtalePeriode) {
      return Promise.reject("Bestiller privat data mangler");
    }
    const validBestilling: PostAvtalePrivatRequest = {
      leverandør: Number(bestilling.leverandoerOrgNr),
      activeFromInclusive: bestilling.avtalePeriode.fromInclusive,
      activeToExclusive: bestilling.avtalePeriode.toExclusive,
      boligType: bestilling.bestillerPrivat.boligType,
      kundenummer: bestilling.bestillerPrivat.kundenummer,
      målerId: bestilling.bestillerPrivat.målerId,
      vilkårAkseptert: true,
      adresse: bestilling.bestillerPrivat.adresse,
      postNummer: Number(bestilling.bestillerPrivat.postNummer),
      sted: bestilling.bestillerPrivat.sted,
    };

    return await mutateBestillingPrivatAsync({
      body: validBestilling,
    });
  };

  const postBestillingSelskap = async () => {
    if (!bestilling.bestillerSelskap || !bestilling.avtalePeriode) {
      return Promise.reject("Bestiller privat data mangler");
    }
    const validBestilling: PostAvtaleSelskapRequest = {
      leverandør: Number(bestilling.leverandoerOrgNr),
      activeFromInclusive: bestilling.avtalePeriode.fromInclusive,
      activeToExclusive: bestilling.avtalePeriode.toExclusive,
      avtaleEier: (bestilling.aktoer as BestillingAktoerSelskap)
        ?.organisasjonsnummer,
      kundenummer: bestilling.bestillerSelskap.kundenummer,
      målerId: bestilling.bestillerSelskap.målerId,
      antallBoenheter: bestilling.bestillerSelskap.antallBoenheter,
      næringsarealProsent: bestilling.bestillerSelskap.næringsarealProsent,
      boligType: bestilling.bestillerSelskap.boligType,
      vilkårAkseptert: true,
    };

    return await mutateBestillingSelskapAsync({
      body: validBestilling,
    });
  };

  async function handleSubmit(_: oppsummeringFormValues) {
    // Stops the form blocker from triggering when we navigate away
    // after submitting the order
    form.resetField("vilkårAkseptert", {
      keepDirty: false,
      defaultValue: true,
    });
    if (bestilling.bestillerPrivat) {
      await postBestillingPrivat()
        .then(async (response) => {
          toast.success({ messageKey: "pages.oppsummering.toast.suksess" });
          await navigate({
            to: "/avtaler/$avtalerId/privat/bekreftelse",
            params: { avtalerId: response.avtale.avtaleId },
          });
        })
        .catch((error) => {
          console.error("Feil ved innsending av privat bestilling", error);
          if (error.status === 409) {
            toast.error({ messageKey: "pages.oppsummering.toast.feil_409" });
            return;
          }
          toast.error({ messageKey: "pages.oppsummering.toast.feil" });
        });
    }
    if (bestilling.bestillerSelskap) {
      await postBestillingSelskap()
        .then(async (response) => {
          toast.success({ messageKey: "pages.oppsummering.toast.suksess" });
          await navigate({
            to: "/avtaler/$avtalerId/selskap/bekreftelse",
            params: { avtalerId: response.avtale.avtaleId },
          });
        })
        .catch((error) => {
          console.error("Feil ved innsending av selskap bestilling", error);
          if (error.status === 409) {
            toast.error({ messageKey: "pages.oppsummering.toast.feil_409" });
            return;
          }
          toast.error({ messageKey: "pages.oppsummering.toast.feil" });
        });
    }
  }

  const updateBestillingContext = (data: oppsummeringFormValues) => {
    setBestillingData({ ...bestilling, vilkårAkseptert: data.vilkårAkseptert });
    return Promise.resolve();
  };

  const leverandrør = bestilling.leverandoerOrgNr
    ? getLeverandoerByOrgNr(bestilling.leverandoerOrgNr)
    : null;
  const aktør =
    bestilling.aktoer && bestilling.aktoer.type === "Selskap"
      ? getAktoerByOrgNr(bestilling.aktoer.organisasjonsnummer)
      : null;

  return (
    <section>
      <FormProvider {...form}>
        <FormNavigationBlocker form={form} onSubmit={updateBestillingContext} />
        <FormWrapper
          onSubmit={form.handleSubmit(handleSubmit)}
          className="space-y-9"
        >
          <section>
            <DescriptionList>
              <DescriptionTerm
                tranlsationFeltKey="pages.oppsummering.bestillingsdato"
                value={formatDate(new Date())}
              />
            </DescriptionList>
          </section>

          <section>
            <HeadingWithLink
              translationKey="pages.oppsummering.felt.leverandør.tittel"
              changeLink="/bestilling/ny/finn-leverandoer"
            />
            <DescriptionList>
              <DescriptionTerm
                tranlsationFeltKey="pages.oppsummering.felt.leverandør.navn"
                value={leverandrør?.navn}
                isLoading={leverandrørIsLoading}
              />
            </DescriptionList>
          </section>

          <section>
            <HeadingWithLink
              translationKey="pages.oppsummering.felt.aktør.tittel"
              changeLink="/bestilling/ny/aktoer"
            />
            <DescriptionList>
              {bestilling.bestillerSelskap && (
                <DescriptionTerm
                  tranlsationFeltKey="pages.oppsummering.felt.aktør.navn"
                  value={aktør && formatAktoerNavn(aktør)}
                  isLoading={aktørIsLoading}
                />
              )}
              <DescriptionTerm
                tranlsationFeltKey="pages.oppsummering.felt.aktør.innsender"
                value={account?.navn}
              />
            </DescriptionList>
          </section>

          <section>
            <HeadingWithLink
              translationKey="pages.oppsummering.felt.avtaleperiode.tittel"
              changeLink="/bestilling/ny/periode"
            />
            <DescriptionList>
              <DescriptionTerm
                tranlsationFeltKey="pages.oppsummering.felt.avtaleperiode.dato"
                value={formatDateStringInterval(
                  bestilling.avtalePeriode?.fromInclusive,
                  bestilling.avtalePeriode?.toExclusive,
                )}
              />
            </DescriptionList>
          </section>

          {bestilling.bestillerPrivat && (
            <section>
              <HeadingWithLink
                translationKey="pages.oppsummering.felt.privat.tittel"
                changeLink="/bestilling/ny/bestiller"
              />
              <DescriptionList>
                <DescriptionTerm
                  tranlsationFeltKey="pages.oppsummering.felt.privat.boligType"
                  value={t("enums.boligType", {
                    context: bestilling.bestillerPrivat.boligType,
                  })}
                />
                <DescriptionTerm
                  tranlsationFeltKey="pages.oppsummering.felt.privat.kundenummer"
                  value={bestilling.bestillerPrivat.kundenummer}
                />
                <DescriptionTerm
                  tranlsationFeltKey="pages.oppsummering.felt.privat.målerId"
                  value={bestilling.bestillerPrivat.målerId}
                />
                <DescriptionTerm
                  tranlsationFeltKey="pages.oppsummering.felt.privat.adresse"
                  value={`${bestilling.bestillerPrivat.adresse}, ${formatPostnummer(bestilling.bestillerPrivat.postNummer)} ${bestilling.bestillerPrivat.sted}`}
                />
              </DescriptionList>
            </section>
          )}

          {bestilling.bestillerSelskap && (
            <section>
              <HeadingWithLink
                translationKey="pages.oppsummering.felt.selskap.tittel"
                changeLink="/bestilling/ny/bestiller"
              />
              <DescriptionList>
                <DescriptionTerm
                  tranlsationFeltKey="pages.oppsummering.felt.selskap.boligType"
                  value={t("enums.boligType", {
                    context: bestilling.bestillerSelskap.boligType,
                  })}
                />
                <DescriptionTerm
                  tranlsationFeltKey="pages.oppsummering.felt.selskap.kundenummer"
                  value={bestilling.bestillerSelskap.kundenummer}
                />
                <DescriptionTerm
                  tranlsationFeltKey="pages.oppsummering.felt.selskap.målerId"
                  value={bestilling.bestillerSelskap.målerId}
                />
                <DescriptionTerm
                  tranlsationFeltKey="pages.oppsummering.felt.selskap.antallBoenheter"
                  value={bestilling.bestillerSelskap.antallBoenheter?.toString()}
                />
                <DescriptionTerm
                  tranlsationFeltKey="pages.oppsummering.felt.selskap.næringsdrivende"
                  value={
                    bestilling.bestillerSelskap.næringsdrivende
                      ? t("enums.jaNei_ja")
                      : t("enums.jaNei_nei")
                  }
                />

                {bestilling.bestillerSelskap.næringsdrivende && (
                  <DescriptionTerm
                    tranlsationFeltKey="pages.oppsummering.felt.selskap.næringsarealProsent"
                    value={formatPercentage(
                      bestilling.bestillerSelskap.næringsarealProsent,
                    )}
                  />
                )}
              </DescriptionList>
            </section>
          )}

          <section>
            <Fieldset className="flex flex-col gap-0">
              <Fieldset.Legend>
                {t("pages.oppsummering.felt.vilkårAkseptert.tittel")}
              </Fieldset.Legend>
              <Link
                href={t("eksterneLenker.avtalevilkaar-og-avbestilling.url")}
                target="_blank"
                rel="noreferrer"
                className="text-base mb-0 mt-1"
              >
                {t("pages.oppsummering.felt.vilkårAkseptert.les-vilkår")}
                <ExternalLinkIcon
                  fontSize={"1.5rem"}
                  className="pl-1"
                  aria-hidden
                />
              </Link>
              <Checkbox
                label={t(
                  "pages.oppsummering.felt.vilkårAkseptert.checkbox-label",
                )}
                {...form.register("vilkårAkseptert")}
                disabled={readOnly}
                className="flex-1 text-base"
              />
            </Fieldset>
          </section>

          <BestillingFormErrorSummary errors={form.formState.errors} />

          {(postPrivatPending || postSelskapPending) && (
            <Dialog.TriggerContext>
              <Dialog open={true} modal closeButton={false} closedby="none">
                <Heading className="flex gap-2 items-center">
                  <Spinner aria-hidden="true" />
                  {t("pages.oppsummering.dialog.oppretter-bestilling")}
                </Heading>
              </Dialog>
            </Dialog.TriggerContext>
          )}
          <BestillingNavigationActions
            currentPageKey={currentPageKey}
            disableNext={readOnly || postPrivatPending || postSelskapPending}
            nextButtonTextKey="pages.oppsummering.send-inn"
          />

          <section className="flex flex-col gap-2 mt-6 text-balder-text-subtle text-sm">
            <Heading level={3} data-size="sm" className="!font-normal">
              {t("pages.oppsummering.personopplysninger.tittel")}
            </Heading>
            <Paragraph className="whitespace-pre-line">
              {t("pages.oppsummering.personopplysninger.beskrivelse")}{" "}
              <Link
                href={t("eksterneLenker.forskriftOmNorgespris.url")}
                target="_blank"
                rel="noreferrer"
              >
                {t("pages.oppsummering.personopplysninger.beskrivelse-lenke")}
                <ExternalLinkIcon
                  fontSize={"1.5rem"}
                  className="pl-1"
                  aria-hidden
                />
              </Link>
            </Paragraph>
            <Paragraph>
              <Link
                href={t("eksterneLenker.cookies.url")}
                target="_blank"
                rel="noreferrer"
              >
                {t("eksterneLenker.cookies.label")}
                <ExternalLinkIcon
                  fontSize={"1.5rem"}
                  className="pl-1"
                  aria-hidden
                />
              </Link>
            </Paragraph>
          </section>
        </FormWrapper>
      </FormProvider>
    </section>
  );
}
